import React, { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Search,
  User,
  Phone,
  Mail,
  MapPin,
  GraduationCap,
  Stethoscope,
  Edit2,
  Trash2,
  Database,
} from "lucide-react";
import axios from "axios";
import API_BASE_URL from "../../../services/api";
import NewDoctorModal from "../../../components/NewDoctorModal";
import EditDoctorModal from "./EditDoctorModal";
import { useAuth } from "../../../context/AuthContext";
import { canAccess } from "../../../utils/permissions";

function Doctors() {
  const { user: currentUser } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  // ✅ Source helper (supports multiple API shapes)
  const getDoctorSource = (doctor) =>
    doctor?.source?.name ||
    doctor?.sourceName ||
    doctor?.source ||
    doctor?.sourceType ||
    "-";

  const loadDoctors = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/doctors`);
      const doctorsData = res.data?.doctors || res.data?.data || res.data || [];
      setDoctors(Array.isArray(doctorsData) ? doctorsData : []);
    } catch (err) {
      console.error("Error fetching doctors:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDoctors();
  }, []);

  // ✅ search filtered list
  const filteredDoctors = useMemo(() => {
    let result = doctors;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((doctor) => {
        const sourceText = String(getDoctorSource(doctor)).toLowerCase();

        return (
          doctor.name?.toLowerCase().includes(term) ||
          doctor.initial?.toLowerCase().includes(term) ||
          doctor.qualification?.toLowerCase().includes(term) ||
          doctor.speciality?.toLowerCase().includes(term) ||
          doctor.mobile?.toLowerCase().includes(term) ||
          doctor.email?.toLowerCase().includes(term) ||
          doctor.venue?.toLowerCase().includes(term) ||
          sourceText.includes(term)
        );
      });
    }

    return result;
  }, [doctors, searchTerm]);

  const handleDoctorSaved = (newDoctor) => {
    setDoctors((prev) => [newDoctor, ...prev]);
  };

  const handleDoctorUpdated = (updatedDoctor) => {
    setDoctors((prev) =>
      prev.map((d) => (d.id === updatedDoctor.id ? updatedDoctor : d))
    );
  };

  const handleEditDoctor = (doctor) => setEditingDoctor(doctor);

  const handleDeleteDoctor = async (id) => {
    if (!window.confirm("Are you sure you want to delete this doctor?")) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/doctors/${id}`);
      setDoctors((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete doctor");
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "-";
    return `+91 ${phone}`;
  };

  return (
    <div className="mx-auto">
      {/* Header */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-sm font-semibold text-gray-900">Doctors</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Manage doctors and their details
          </p>
        </div>

        <button
          onClick={() => canAccess(currentUser, "canCreate") && setOpenModal(true)}
          disabled={!canAccess(currentUser, "canCreate")}
          className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          Add Doctor
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded border border-gray-200 mb-4">
        <div className="p-3 border-b border-gray-200">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, qualification, speciality, mobile, source..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-10">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2"></div>
              <p className="text-xs text-gray-500">Loading doctors...</p>
            </div>
          ) : filteredDoctors.length === 0 ? (
            <div className="text-center py-10">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-gray-100 rounded-full mb-3">
                <User className="w-5 h-5 text-gray-400" />
              </div>
              <h3 className="text-xs font-medium text-gray-900 mb-1">
                No doctors found
              </h3>
              <p className="text-xs text-gray-500">
                {searchTerm ? "Try adjusting your search" : "Add your first doctor"}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="py-2.5 px-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                    Doctor Details
                  </th>
                  <th className="py-2.5 px-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                    Contact & Qualification
                  </th>
                  {/* ✅ NEW SOURCE COLUMN */}
                  <th className="py-2.5 px-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                    Source
                  </th>
                  <th className="py-2.5 px-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200">
                {filteredDoctors.map((doctor) => (
                  <tr
                    key={doctor.id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    {/* Doctor Details */}
                    <td className="py-3 px-3">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-50 rounded flex items-center justify-center">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="ml-2.5">
                          <h3 className="text-xs font-medium text-gray-900">
                            {doctor.initial
                              ? `${doctor.initial} ${doctor.name}`
                              : doctor.name}
                          </h3>

                          {doctor.number && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              ID: {doctor.number}
                            </p>
                          )}

                          {doctor.venue && (
                            <div className="flex items-center mt-1">
                              <MapPin className="w-2.5 h-2.5 text-gray-400 mr-1" />
                              <p className="text-xs text-gray-500 truncate max-w-[220px]">
                                {doctor.venue}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Contact & Qualification */}
                    <td className="py-3 px-3">
                      <div className="space-y-1.5">
                        <div className="flex items-center">
                          <Phone className="w-2.5 h-2.5 text-gray-400 mr-1.5" />
                          <a
                            href={doctor.mobile ? `tel:+91${doctor.mobile}` : undefined}
                            className="text-xs text-gray-700 hover:text-blue-600 truncate max-w-[160px]"
                            title={doctor.mobile ? `+91 ${doctor.mobile}` : ""}
                          >
                            {formatPhoneNumber(doctor.mobile)}
                          </a>
                        </div>

                        {doctor.email && (
                          <div className="flex items-center">
                            <Mail className="w-2.5 h-2.5 text-gray-400 mr-1.5" />
                            <a
                              href={`mailto:${doctor.email}`}
                              className="text-xs text-blue-600 hover:text-blue-800 hover:underline truncate max-w-[180px]"
                              title={doctor.email}
                            >
                              {doctor.email}
                            </a>
                          </div>
                        )}

                        {doctor.qualification && (
                          <div className="flex items-center">
                            <GraduationCap className="w-2.5 h-2.5 text-gray-400 mr-1.5" />
                            <span className="text-xs text-gray-700 truncate max-w-[200px]">
                              {doctor.qualification}
                            </span>
                          </div>
                        )}

                        {doctor.speciality && (
                          <div className="flex items-center">
                            <Stethoscope className="w-2.5 h-2.5 text-gray-400 mr-1.5" />
                            <span className="text-xs text-gray-700 truncate max-w-[200px]">
                              {doctor.speciality}
                              {doctor.specialityType
                                ? ` (${doctor.specialityType})`
                                : ""}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* ✅ Source */}
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-gray-50 rounded flex items-center justify-center">
                          <Database className="w-3.5 h-3.5 text-gray-500" />
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-900">
                            {getDoctorSource(doctor)}
                          </div>
                          {doctor.sourceDetails && (
                            <div className="text-[11px] text-gray-500">
                              {doctor.sourceDetails}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="py-3 px-3">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => canAccess(currentUser, "canEdit") && handleEditDoctor(doctor)}
                          disabled={!canAccess(currentUser, "canEdit")}
                          className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Edit Doctor"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => canAccess(currentUser, "canDelete") && handleDeleteDoctor(doctor.id)}
                          disabled={!canAccess(currentUser, "canDelete")}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Delete Doctor"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        {filteredDoctors.length > 0 && !loading && (
          <div className="px-3 py-2 border-t border-gray-200 flex items-center justify-between">
            <div className="text-xs text-gray-500">
              Showing{" "}
              <span className="font-medium">{filteredDoctors.length}</span> of{" "}
              <span className="font-medium">{doctors.length}</span> doctors
            </div>

            {/* simple pagination placeholder */}
            <div className="flex items-center space-x-1">
              <button
                disabled
                className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-400 rounded cursor-not-allowed"
              >
                ←
              </button>
              <button className="px-2 py-1 text-xs font-medium bg-blue-600 text-white rounded">
                1
              </button>
              <button
                disabled
                className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-400 rounded cursor-not-allowed"
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <NewDoctorModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleDoctorSaved}
      />

      <EditDoctorModal
        isOpen={!!editingDoctor}
        onClose={() => setEditingDoctor(null)}
        onSave={handleDoctorUpdated}
        doctor={editingDoctor}
      />
    </div>
  );
}

export default Doctors;
